import { AutoComplete, Form } from 'antd';
import { FormInstance, Rule } from 'antd/es/form';
import { OptionEntity } from 'domain/entities/OptionEntity';
import React, { SelectHTMLAttributes, useEffect, useState } from 'react';
import getMessage from 'utilities/constants/MESSAGE';
import { PLACEHOLDER } from 'utilities/constants/PLACEHOLDER';

export interface SelectProps extends SelectHTMLAttributes<HTMLDivElement> {
    options: OptionEntity[];
    size?: any;
    name?: string;
    disabled?: any;
    required?: boolean;
    requiredMessage?: string;
    callback?: any;
    rules?: Rule[];
    label?: string;
    labelClassName?: string;
    icon?: React.ReactNode;
    onChange?: (value: any) => void;
    onSelect?: (value: any) => void;
    onSearch?: (value: any) => void;
    maxLength?: number;
    filterOption?: (inputValue: string, option: any) => boolean;
    notFoundContent?: any;
    currentForm?: FormInstance<any>;
    onValidateError?: (isError?: boolean) => void;
    onBlurEvent?: (value: any) => void;
    allowClear?: boolean;
    placeholder: string;
}

const defaultFilterOption = (inputValue: string, option: any) => {
    return option?.label?.indexOf(inputValue) !== -1;
};

export const FormInputAutoComplete = ({
    options,
    rules = [],
    label,
    required,
    requiredMessage,
    labelClassName = '',
    name,
    placeholder = PLACEHOLDER.TEXT,
    size = 'large',
    onChange,
    onSearch,
    onSelect,
    filterOption = defaultFilterOption,
    notFoundContent,
    currentForm,
    onValidateError,
    onBlurEvent,
    allowClear = true,
    ...props
}: SelectProps) => {
    const [currentSearch, setCurrentSearch] = useState<any>();

    useEffect(() => {
        if (currentForm && onValidateError) {
            currentForm
                .validateFields([name || ''], {
                    validateOnly: currentForm.getFieldError(name || '').length <= 0,
                })
                .then(() => {
                    onValidateError(false);
                })
                .catch(() => {
                    onValidateError(true);
                });
        }
    }, [rules]);

    useEffect(() => {
        setCurrentSearch(currentForm?.getFieldValue(name));
    }, [currentForm?.getFieldValue(name)]);

    return (
        <>
            <span className={labelClassName}>
                {label}
                {label && required ? <span style={{ color: '#EB5757' }}>*</span> : <></>}
            </span>
            <Form.Item
                required={required}
                className={props.className}
                name={name}
                label={false}
                rules={rules.concat([
                    {
                        required: required ? true : false,
                        message: requiredMessage ?? getMessage('Sel_Required', label),
                    },
                ])}
            >
                <AutoComplete
                    {...props}
                    showSearch
                    onSelect={(val) => {
                        const option = options.find((option: any) => {
                            return option.value == val;
                        });
                        setCurrentSearch(option?.label);
                        if (onSelect) {
                            onSelect(option);
                        }
                    }}
                    filterOption={filterOption}
                    notFoundContent={notFoundContent}
                    allowClear={allowClear}
                    size={size}
                    className="select-custom "
                    options={options}
                    placeholder={placeholder}
                    onSearch={(val: any) => {
                        const option = options.find((option: any) => {
                            return option.label.toLowerCase().includes(val.toLowerCase());
                        });
                        setCurrentSearch(val);
                        if (onSearch) onSearch(option);
                    }}
                    onBlur={() => {
                        if (onBlurEvent) {
                            const option = options.find((option: any) => {
                                return option.label == currentSearch;
                            });
                            onBlurEvent(option);
                        }
                    }}
                    onChange={(value) => {
                        if (onChange) {
                            onChange(value);
                        }
                        props.callback && props.callback(value);
                    }}
                />
            </Form.Item>
        </>
    );
};
